import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { toast } from 'react-hot-toast';
import { MinusIcon, PlusIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

// Redux
import { setOrderDataAction } from '../../../redux/Actions/CreateOrderActions';
import { getServicesAndPricingApi } from '../../../redux/Reducers/commonApis';

// Utils
import { amountCheckFn } from '../../../utils/commonUtil';

// Components
import OrderPreferencesModule from '../../../components/OrderPreferencesModule';
import { Section, LinkButton, Placeholder, LoadingRow } from './ui';
import { SECTION_IDS, isOk, isPickupInstructionMissing, preferencesSummary } from './helpers';

const tenant = import.meta.env.VITE_TENANT_NAME;
const defaultQty = (serviceId) => (tenant === 'pushlaundry' && serviceId === 13 ? 15 : 1);

// Quantity stepper that lets the user clear the field while typing without dropping the service.
function QtyStepper({ value, onChange }) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  const commit = (raw) => {
    const n = parseInt(raw, 10);
    if (Number.isInteger(n) && n >= 0) onChange(n);
    else setDraft(String(value));
  };

  return (
    <div className='flex items-center rounded-md border border-slate-200 bg-white' onClick={(e) => e.stopPropagation()}>
      <button
        type='button'
        aria-label='Decrease quantity'
        className='flex h-8 w-8 items-center justify-center rounded-l-md text-slate-500 hover:bg-slate-50'
        onClick={() => onChange(value - 1)}
      >
        <MinusIcon className='h-3.5 w-3.5' />
      </button>
      <input
        type='text'
        inputMode='numeric'
        aria-label='Quantity'
        className='h-8 w-10 border-0 p-0 text-center text-sm font-semibold text-slate-900 focus:ring-0'
        value={draft}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, '');
          setDraft(v);
          if (v !== '' && +v > 0) onChange(+v);
        }}
        onBlur={(e) => commit(e.target.value)}
      />
      <button
        type='button'
        aria-label='Increase quantity'
        className='flex h-8 w-8 items-center justify-center rounded-r-md text-slate-500 hover:bg-slate-50'
        onClick={() => onChange(value + 1)}
      >
        <PlusIcon className='h-3.5 w-3.5' />
      </button>
    </div>
  );
}

export default function ServicesSection() {
  const dispatch = useDispatch();
  const { orderServiceAddress, selectedServices, orderPreferences } = useSelector((s) => s.createNewOrder);
  const preference = useSelector((s) => s.myaccount.orderPreferences?.preference);

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editPrefs, setEditPrefs] = useState(false);

  // Services & pricing are per address.
  useEffect(() => {
    if (!orderServiceAddress?.id) return;
    let stale = false;
    setLoading(true);
    getServicesAndPricingApi(orderServiceAddress.id).then((res) => {
      if (stale) return;
      setLoading(false);
      if (isOk(res)) {
        const list = (res?.data || []).map((s) => ({
          id: s.id,
          service: s.name,
          unit: s.unit_type,
          client_fee: s.client_fee,
          priority_fee: s.priority_fee,
          image: s.image?.absolute_url || null,
        }));
        setServices(list);
        dispatch(setOrderDataAction({ availableServices: list }));
        if (list.length && isEmpty(selectedServices))
          dispatch(
            setOrderDataAction({ selectedServices: [{ service_id: list[0].id, quantity: defaultQty(list[0].id) }] })
          );
      } else {
        setServices([]);
        toast.error(res?.message || 'Something went wrong');
      }
    });
    return () => {
      stale = true;
    };
  }, [orderServiceAddress?.id]);

  // Address preferences become this order's preferences.
  useEffect(() => {
    if (preference == null) {
      dispatch(setOrderDataAction({ orderPreferences: undefined }));
      return;
    }
    const p = { ...preference };
    if (
      String(p.pickup_delivery || '')
        .toLowerCase()
        .trim() !== 'other'
    )
      delete p.pickup_delivery_instructions;
    dispatch(setOrderDataAction({ orderPreferences: p }));
  }, [preference]);

  const qtyOf = (id) => selectedServices?.find((s) => s.service_id === id)?.quantity || 0;
  const setQty = (id, qty) => {
    const rest = (selectedServices || []).filter((s) => s.service_id !== id);
    if (qty <= 0) dispatch(setOrderDataAction({ selectedServices: rest }));
    else if (qtyOf(id)) {
      dispatch(
        setOrderDataAction({
          selectedServices: selectedServices.map((s) => (s.service_id === id ? { ...s, quantity: qty } : s)),
        })
      );
    } else
      dispatch(
        setOrderDataAction({ selectedServices: [...(selectedServices || []), { service_id: id, quantity: qty }] })
      );
  };

  const instructionsMissing = isPickupInstructionMissing(orderPreferences);
  const count = selectedServices?.length || 0;
  const disabled = !orderServiceAddress;

  return (
    <Section
      id={SECTION_IDS.services}
      step={2}
      title='Services'
      subtitle={count ? `${count} selected` : null}
      summary={
        (selectedServices || [])
          .map((s) => {
            const svc = services.find((x) => x.id === s.service_id);
            return svc ? `${svc.service} × ${s.quantity}` : null;
          })
          .filter(Boolean)
          .join(', ') || null
      }
      done={count > 0 && !instructionsMissing}
      disabled={disabled}
    >
      {disabled ? (
        <Placeholder>Choose a pickup address to see services and pricing.</Placeholder>
      ) : loading ? (
        <LoadingRow text='Loading services…' />
      ) : services.length === 0 ? (
        <Placeholder>No services are available at this address.</Placeholder>
      ) : (
        <div className='grid grid-cols-1 gap-2 md:grid-cols-2'>
          {services.map((svc) => {
            const qty = qtyOf(svc.id);
            const selected = qty > 0;
            return (
              <div
                key={svc.id}
                className={`flex items-center gap-3 rounded-md border p-2.5 transition ${
                  selected ? 'border-client-300 bg-client-300/5 ring-client-300 ring-1' : 'border-slate-200'
                }`}
              >
                {svc.image ? (
                  <img
                    src={svc.image}
                    alt=''
                    className='h-10 w-10 shrink-0 rounded-md bg-slate-50 object-contain p-1'
                  />
                ) : (
                  <div className='h-10 w-10 shrink-0 rounded-md bg-slate-100' />
                )}
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-semibold text-slate-900'>{svc.service}</p>
                  <p className='text-xs text-slate-500'>
                    {amountCheckFn(svc.client_fee)} / {svc.unit}
                  </p>
                </div>
                {selected ? (
                  <QtyStepper value={qty} onChange={(n) => setQty(svc.id, n)} />
                ) : (
                  <button
                    type='button'
                    onClick={() => setQty(svc.id, defaultQty(svc.id))}
                    className='hover:border-client-300 hover:text-client-300 flex h-8 items-center gap-1 rounded-md border border-slate-200 px-3 text-xs font-semibold text-slate-600 transition'
                  >
                    <PlusIcon className='h-3.5 w-3.5' />
                    Add
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!disabled && !loading && services.length > 0 && (
        <div
          className={`mt-3 rounded-md border px-3 py-2.5 ${
            instructionsMissing ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-slate-50/60'
          }`}
        >
          <div className='flex items-center gap-2'>
            <AdjustmentsHorizontalIcon className='h-4 w-4 shrink-0 text-slate-400' />
            <p className='min-w-0 flex-1 truncate text-xs text-slate-600'>
              <span className='font-semibold text-slate-700'>Preferences: </span>
              {preferencesSummary(orderPreferences)}
            </p>
            <LinkButton onClick={() => setEditPrefs((v) => !v)}>{editPrefs ? 'Done' : 'Edit'}</LinkButton>
          </div>
          {instructionsMissing && !editPrefs && (
            <p className='mt-1 pl-6 text-xs text-red-600'>Add pickup instructions (at least 5 characters).</p>
          )}
          {editPrefs && (
            <div className='-mx-2 mt-2'>
              <OrderPreferencesModule
                type='edit'
                preferences={orderPreferences}
                update={(obj) => dispatch(setOrderDataAction({ orderPreferences: obj }))}
              />
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
